/**
 * Why a Gmail connection attempt was refused, and what the parent can do next.
 *
 * Produced by `StepAutoForwarding` (the client tokeninfo check plus the
 * `gmail-forwarding-setup` verdicts) and rendered inline by
 * `StepSetupForwarding` on the connect screen the parent is routed back to.
 */
export type ForwardingBlock =
  | { reason: 'non_personal_domain'; email?: string }
  // A partial permission grant: the parent left one or more of the requested
  // scopes switched off in Google's consent screen.
  | { reason: 'missing_scopes' }
  // Right session, wrong mailbox picked in Google's account chooser
  // (YAR-325 Gate 2.5).
  | { reason: 'account_mismatch'; signedInEmail?: string; email?: string };

export type ForwardingBlockReason = ForwardingBlock['reason'];

/**
 * Can the parent clear this block by running Google's consent flow again on
 * the account they are ALREADY signed in to Yaruta with?
 *
 * Single source of truth for three decisions that must agree: the primary
 * CTA's label, what that CTA does (re-run OAuth vs. sign out), and whether the
 * screen keeps its Back affordance. Those used to be three independent
 * `reason !== 'account_mismatch'` checks, and `missing_scopes` fell on the
 * wrong side of all three — a parent who unchecked one permission box was
 * offered only "Sign in with a different account", which signed them out, so
 * the account they actually wanted could never be retried (YAR-372).
 *
 * Deliberately an exhaustive switch with no `default`: `strictNullChecks`
 * makes a new, unhandled reason a COMPILE error here rather than silently
 * defaulting to "sign this parent out".
 */
export function isRetryableWithSameAccount(
  reason: ForwardingBlockReason,
): boolean {
  switch (reason) {
    // `missing_scopes` — a partial grant. `requestGmailForwardingAccess` sends
    //   `prompt=consent`, so Google re-renders the permission checkboxes on
    //   every attempt and the parent can turn on what they left off; the
    //   scopes already granted do not have to be revoked first.
    // `account_mismatch` — one correct pick in Google's chooser fixes it, and
    //   signing the parent out would destroy a perfectly good session to undo
    //   a two-click mistake.
    case 'missing_scopes':
    case 'account_mismatch':
      return true;
    // The signed-in account is a Workspace / business address. Yaruta connects
    // the mailbox you are signed in with (YAR-325), so there is nothing to
    // retry: the only recovery is to come back as an account that owns a
    // personal Gmail.
    case 'non_personal_domain':
      return false;
  }
}
